/**
 * 🔐 PAGE DE VÉRIFICATION EMAIL CLIENT
 * Appelée quand le client clique sur le lien dans son email
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader, Mail, LogIn, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyCustomerEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [customerName, setCustomerName] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        return;
      }

      try {
        const response = await fetch(`${API}/api/customers/verify-email?token=${token}`);
        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.detail || 'Erreur de vérification');
        }
        
        setCustomerName(result.customerName || '');
        setStatus('success');
      } catch (error) {
        console.error('Erreur vérification:', error);
        setStatus('error');
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-x-hidden overflow-y-auto"
      style={{ 
        background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e1b4b 100%)' 
      }}
    >
      {/* Stars background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-white"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.5 + 0.2
            }}
            animate={{
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.5, 1]
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="text-center max-w-md w-full relative z-10"
      >
        {status === 'loading' && (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="inline-block mb-6"
            >
              <Loader className="w-16 h-16 text-cyan-400" />
            </motion.div>
            <h1 className="text-2xl font-bold text-white">Vérification en cours...</h1>
            <p className="text-gray-400 mt-2">Connexion à la station orbitale...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="mb-6"
            >
              <motion.div
                animate={{
                  boxShadow: [
                    '0 0 20px rgba(34, 197, 94, 0.5)',
                    '0 0 60px rgba(34, 197, 94, 0.8)',
                    '0 0 20px rgba(34, 197, 94, 0.5)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-500 to-emerald-600"
              >
                <CheckCircle className="w-14 h-14 text-white" />
              </motion.div>
            </motion.div>
            
            <h1 className="text-3xl font-bold text-white mb-2">
              Email vérifié ! 🎉
            </h1>
            
            <p className="text-gray-300 mb-6">
              Bravo {customerName || 'Commandant'} ! Tu peux maintenant te connecter avec ton email.
            </p>
            
            <motion.div 
              className="p-6 rounded-2xl mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                border: '2px solid rgba(6, 182, 212, 0.5)',
                boxShadow: '0 0 30px rgba(6, 182, 212, 0.3)'
              }}
            >
              <div className="flex items-center justify-center gap-2 mb-3">
                <Mail className="w-6 h-6 text-cyan-400" />
                <span className="text-xl text-white font-semibold">Connexion Email Activée</span>
              </div>
              
              <div className="text-left space-y-2 text-sm text-gray-300">
                <p>✅ Te connecter avec ton email au lieu du téléphone</p>
                <p>✅ Récupérer ton compte plus facilement</p>
                <p>✅ Ne jamais perdre l'accès à ton espace VIP</p>
              </div>
            </motion.div>

            <motion.button
              onClick={() => navigate('/client')}
              className="w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%)',
                boxShadow: '0 0 20px rgba(6, 182, 212, 0.4)'
              }}
              whileHover={{ scale: 1.02, boxShadow: '0 0 30px rgba(6, 182, 212, 0.6)' }}
              whileTap={{ scale: 0.98 }}
            >
              <LogIn className="w-5 h-5" />
              Accéder à mon espace VIP
            </motion.button>
          </>
        )}

        {status === 'error' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="mb-6"
            >
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-red-500 to-orange-600">
                <XCircle className="w-14 h-14 text-white" />
              </div>
            </motion.div>
            
            <h1 className="text-3xl font-bold text-white mb-4">Lien invalide</h1>
            <p className="text-gray-300 mb-6">
              Ce lien de vérification est invalide ou a expiré.
            </p>
            
            <div className="space-y-3">
              <motion.button
                onClick={() => navigate('/client')}
                className="w-full py-3 px-6 rounded-xl font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%)' }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Connecte-toi pour renvoyer le lien
              </motion.button>
              
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 px-6 rounded-xl font-bold text-gray-400 border border-gray-600 hover:bg-white/5"
              >
                Retour à l'accueil
              </button>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="mt-8">
          <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4 text-cyan-500/50" />
            La Petite Épicerie • Station Spatiale VIP
          </p>
        </div>
      </motion.div>
    </div>
  );
}
